// @flow
import React, {type Node, Fragment} from 'react'
import {type RelayFragmentContainer, createFragmentContainer, graphql} from 'react-relay'

import Leaf from 'components/ui/icons/ios/Leaf'
import Contact from 'components/ui/icons/md/Contact'
import HolacracyMark from 'components/ui/icons/HolacracyMark'
import Call from 'components/ui/icons/ios/Call'
import Book from 'components/ui/icons/md/Book'
import Present from 'components/ui/icons/Present'
import QuestionMark from 'components/ui/icons/QuestionMark'
import IconButton from 'components/SideBar/ui/IconButton'
import DropdownSection, {type RenderButtonProps} from 'components/SideBar/ui/DropdownSection'
import SectionItem from 'components/SideBar/ui/SectionItem'
import type {
  Help_organization as Organization,
} from './__generated__/Help_organization.graphql'

type Props = $ReadOnly<{
  organization: Organization,
}>

function Help({organization}: Props): Node {
  const viewer = organization.viewer
  const renderButton = ({toggle}: RenderButtonProps) => (
    <IconButton
      alt={I18n.t('side_nav.settings_section.help.title')}
      onClick={toggle}
      theme="dark"
    >
      <QuestionMark />
    </IconButton>
  )

  return (
    <DropdownSection
      renderButton={renderButton}
      title={I18n.t('side_nav.settings_section.help.support')}
      sectionName="settings_dropdown"
      renderChildren={() => (
        <Fragment>
          <SectionItem
            renderIcon={() => (<Call />)}
            title={I18n.t('nav.get_support')}
            href={`/organizations/${organization.databaseId}/dialogs/support`}
            dataTurboFrame="remote-modal"
            dark
          />
          <SectionItem
            renderIcon={() => (<Book />)}
            title={I18n.t('side_nav.settings_section.help.knowledge_base')}
            dark
            href={viewer.routes.knowledgebaseUrl}
            target="_blank"
          />
          <SectionItem
            renderIcon={() => (<Leaf />)}
            title={I18n.t('holacracy_help.holacracy_habits.short_title')}
            dark
            href={viewer.routes.holacracyHabitsPath}
          />
          <SectionItem
            renderIcon={() => (<Contact />)}
            title={I18n.t('holacracy_help.office_hours.title')}
            dark
            href={`${viewer.routes.coachQAPath}?organization_id=${organization.databaseId}`}
          />
          <SectionItem
            renderIcon={() => (<Present />)}
            title={I18n.t('nav.whats_new')}
            dark
            href={viewer.routes.whatsNewPath}
            target="_blank"
          />
          <SectionItem
            renderIcon={() => (<HolacracyMark />)}
            title={I18n.t('accounts.welcome.holacracy_resources')}
            dark
            href={viewer.routes.holacracyResourcesUrl}
            target="_blank"
          />
        </Fragment>
      )}
    />
  )
}

export default (createFragmentContainer(Help, {
  organization: graphql`
    fragment Help_organization on Organization {
      databaseId

      viewer {
        routes {
          coachQAPath
          holacracyHabitsPath
          holacracyResourcesUrl
          knowledgebaseUrl
          whatsNewPath
        }
      }
    }
  `,
}): RelayFragmentContainer<typeof(Help)>)
